import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { COPY_API_MODELS } from "@shared/copy-api-models";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FlaskConical, Sparkles, Copy, Check, Info, RefreshCw, AlertTriangle, X, Loader2 } from "lucide-react";

interface GenerateResult {
  versions: string[];
  model: string;
  generatedAt: string;
}

// One stacked test run (success with three variants, or a failure).
interface RunResult {
  id: string;
  requestedModel: string;
  ok: boolean;
  versions?: string[];
  modelUsed?: string;
  error?: string;
  at: string;
  seconds: number;
}

const REQUIRED = <span className="text-red-500">*</span>;
// Sentinel for the "run every model" dropdown option (client-only meta value).
const ALL_MODELS = "__all__";

export default function CopyApiTester() {
  const { toast } = useToast();

  // The saved config model is the default; an admin can override it here just
  // for testing, without changing the live production config.
  const { data: savedSettings } = useQuery<any>({ queryKey: ["/api/copy-api-settings"] });
  const [model, setModel] = useState<string>("");
  const effectiveModel = model || savedSettings?.model || "gpt-5.2";

  const [sponsorName, setSponsorName] = useState("");
  const [copyType, setCopyType] = useState<"broadcast" | "event">("broadcast");
  // Broadcast
  const [website, setWebsite] = useState("");
  const [moreInfoLink, setMoreInfoLink] = useState("");
  // Event
  const [eventName, setEventName] = useState("");
  const [eventWebsite, setEventWebsite] = useState("");
  const [eventDatesAndTimes, setEventDatesAndTimes] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [ticketsLink, setTicketsLink] = useState("");
  // Shared
  const [preamble, setPreamble] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [pronunciationNotes, setPronunciationNotes] = useState("");
  const [campaignGoals, setCampaignGoals] = useState("");

  // Each Generate/Regenerate appends a run so models can be compared on the
  // same input. Newest first.
  const [runs, setRuns] = useState<RunResult[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const generate = useMutation<GenerateResult, Error, { regenerate: boolean; model: string }, { startedAt: number }>({
    onMutate: () => ({ startedAt: Date.now() }),
    mutationFn: async ({ regenerate, model: reqModel }) => {
      // Send only the active mode's fields plus the shared ones; omit empties.
      // The server schema drops any value that is not a valid URL, so bad input
      // never 400s the request.
      const body: Record<string, any> = {
        sponsorName: sponsorName.trim(),
        copyType,
        model: reqModel,
      };
      if (preamble.trim()) body.preamble = preamble.trim();
      if (additionalDetails.trim()) body.additionalDetails = additionalDetails.trim();
      if (pronunciationNotes.trim()) body.pronunciationNotes = pronunciationNotes.trim();
      if (campaignGoals.trim()) body.campaignGoals = campaignGoals.trim();

      if (copyType === "broadcast") {
        if (website.trim()) body.website = website.trim();
        if (moreInfoLink.trim()) body.moreInfoLink = moreInfoLink.trim();
      } else {
        if (eventName.trim()) body.eventName = eventName.trim();
        if (eventWebsite.trim()) body.eventWebsite = eventWebsite.trim();
        if (eventDatesAndTimes.trim()) body.eventDatesAndTimes = eventDatesAndTimes.trim();
        if (eventLocation.trim()) body.eventLocation = eventLocation.trim();
        if (ticketsLink.trim()) body.ticketsLink = ticketsLink.trim();
      }

      if (regenerate) {
        // Avoid every variant already shown in this session (cap 12).
        const priors = runs.flatMap((r) => r.versions ?? []).slice(0, 12);
        if (priors.length > 0) body.previousVariants = priors;
      }

      return apiRequest("/api/copy-api-test", "POST", body);
    },
    onSuccess: (data, vars, context) => {
      setRuns((prev) => [
        {
          id: crypto.randomUUID(),
          requestedModel: vars.model,
          ok: true,
          versions: data.versions,
          modelUsed: data.model,
          at: new Date().toLocaleTimeString(),
          seconds: (Date.now() - context.startedAt) / 1000,
        },
        ...prev,
      ]);
    },
    onError: (error, vars, context) => {
      setRuns((prev) => [
        {
          id: crypto.randomUUID(),
          requestedModel: vars.model,
          ok: false,
          error: error.message || "Generation failed",
          at: new Date().toLocaleTimeString(),
          seconds: context ? (Date.now() - context.startedAt) / 1000 : 0,
        },
        ...prev,
      ]);
    },
  });

  // When "Generate with each model" is selected, run every real model in turn.
  const [runningAll, setRunningAll] = useState(false);
  const isAllMode = effectiveModel === ALL_MODELS;
  const busy = generate.isPending || runningAll;

  // Mirror the Copy Engine: event copy requires Event Location + Tickets link.
  const eventReady = copyType !== "event" || (!!eventLocation.trim() && !!ticketsLink.trim());
  const canGenerate = !!sponsorName.trim() && eventReady && !busy;
  const hasSuccessfulRun = runs.some((r) => r.ok);

  // Run each model sequentially on the same input; each appends a stacked run.
  // Sequential (not parallel) keeps us gentle on provider rate limits and shows
  // results progressively. A failed model is recorded and we continue.
  const runAllModels = async () => {
    setRunningAll(true);
    try {
      for (const m of COPY_API_MODELS) {
        try {
          await generate.mutateAsync({ regenerate: false, model: m.value });
        } catch {
          /* onError already recorded this model's failed run; keep going */
        }
      }
    } finally {
      setRunningAll(false);
    }
  };

  const handleGenerate = () => {
    if (isAllMode) runAllModels();
    else generate.mutate({ regenerate: false, model: effectiveModel });
  };

  const copyVariant = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Select and copy the text manually.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Copy API Test
          </CardTitle>
          <CardDescription>
            Run the public Copy API prompt with sample inputs. This calls the exact same generation path as the
            live endpoint, so it is a faithful preview for iterating on the prompt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Uses the prompt and settings saved under Copy API; you can override the MODEL below just for this
              test (it does not change the saved config). Each run is added below so you can compare models on the
              same input. No fallback: if a model fails, the run shows the real error. Does not consume the API key
              daily quota.
            </AlertDescription>
          </Alert>

          <div className="space-y-2 md:max-w-md">
            <Label htmlFor="test-model">Model (test override)</Label>
            <Select value={effectiveModel} onValueChange={setModel}>
              <SelectTrigger id="test-model" data-testid="select-test-model">
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_MODELS}>Generate with each model</SelectItem>
                {COPY_API_MODELS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {isAllMode
                ? "Runs every model once on the same input, stacking the results for comparison."
                : `Defaults to the saved config model (${savedSettings?.model ?? "..."}). Overriding here affects only this test.`}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="test-sponsor">Sponsor Name {REQUIRED}</Label>
              <Input
                id="test-sponsor"
                placeholder="e.g., Acme Coffee Roasters"
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                data-testid="input-test-sponsor-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-copytype">Copy Type {REQUIRED}</Label>
              <Select value={copyType} onValueChange={(v) => setCopyType(v as "broadcast" | "event")}>
                <SelectTrigger id="test-copytype" data-testid="select-test-copy-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="broadcast">Broadcast</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Broadcast-only fields */}
          {copyType === "broadcast" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="test-website">Sponsor Website (fetched for context)</Label>
                <Input
                  id="test-website"
                  placeholder="acme.com or https://acme.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  data-testid="input-test-website"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-moreinfo">More Info Link (reference only)</Label>
                <Input
                  id="test-moreinfo"
                  placeholder="https://acme.com/promo"
                  value={moreInfoLink}
                  onChange={(e) => setMoreInfoLink(e.target.value)}
                  data-testid="input-test-more-info"
                />
              </div>
            </div>
          )}

          {/* Event-only fields */}
          {copyType === "event" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="test-event-name">Event Name</Label>
                <Input
                  id="test-event-name"
                  placeholder="e.g., Summer Jazz Festival"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  data-testid="input-test-event-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-event-website">Event Website (fetched for context)</Label>
                <Input
                  id="test-event-website"
                  placeholder="event.com or https://event.com"
                  value={eventWebsite}
                  onChange={(e) => setEventWebsite(e.target.value)}
                  data-testid="input-test-event-website"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-event-dates">Event Dates and Times</Label>
                <Input
                  id="test-event-dates"
                  placeholder="e.g., Sat Aug 16, 6-10pm"
                  value={eventDatesAndTimes}
                  onChange={(e) => setEventDatesAndTimes(e.target.value)}
                  data-testid="input-test-event-dates"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-event-location">Event Location {REQUIRED}</Label>
                <Input
                  id="test-event-location"
                  placeholder="e.g., Riverside Park, Springfield"
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  data-testid="input-test-event-location"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="test-tickets">Tickets / Info Link {REQUIRED} (reference only)</Label>
                <Input
                  id="test-tickets"
                  placeholder="https://event.com/tickets"
                  value={ticketsLink}
                  onChange={(e) => setTicketsLink(e.target.value)}
                  data-testid="input-test-tickets-link"
                />
              </div>
            </div>
          )}

          {/* Shared fields */}
          <div className="space-y-2">
            <Label htmlFor="test-preamble">Station Preamble</Label>
            <Textarea
              id="test-preamble"
              placeholder="Station boilerplate to carry into the copy..."
              value={preamble}
              onChange={(e) => setPreamble(e.target.value)}
              className="min-h-[80px]"
              data-testid="input-test-preamble"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="test-details">Additional Details</Label>
            <Textarea
              id="test-details"
              placeholder="Any extra context the copy should reflect..."
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
              className="min-h-[80px]"
              data-testid="input-test-additional-details"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="test-pronunciation">Pronunciation Notes</Label>
            <Textarea
              id="test-pronunciation"
              placeholder="How to pronounce names, e.g. 'Acme = AK-mee'..."
              value={pronunciationNotes}
              onChange={(e) => setPronunciationNotes(e.target.value)}
              className="min-h-[80px]"
              data-testid="input-test-pronunciation-notes"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="test-campaign-goals">Campaign Goals</Label>
            <Textarea
              id="test-campaign-goals"
              placeholder="What the campaign is trying to achieve..."
              value={campaignGoals}
              onChange={(e) => setCampaignGoals(e.target.value)}
              className="min-h-[80px]"
              data-testid="input-test-campaign-goals"
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            {copyType === "event" && !eventReady && (
              <span className="text-xs text-gray-500">Event Location and Tickets link are required for event copy.</span>
            )}
            {hasSuccessfulRun && !isAllMode && (
              <Button
                variant="outline"
                onClick={() => generate.mutate({ regenerate: true, model: effectiveModel })}
                disabled={!canGenerate}
                data-testid="button-regenerate-copy-test"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate (different)
              </Button>
            )}
            <Button
              onClick={handleGenerate}
              disabled={!canGenerate}
              data-testid="button-run-copy-test"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {busy
                ? (isAllMode ? "Running all models..." : "Generating...")
                : (isAllMode ? "Generate with each model" : "Generate 3 Variants")}
            </Button>
          </div>
          {busy && (
            <p className="text-xs text-gray-500 text-right">
              Running {generate.variables?.model ?? effectiveModel}. Web-fetch models (Claude / Gemini) can take 30-90 seconds.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Stacked results: newest first, compare models on the same input */}
      {(runs.length > 0 || busy) && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-600">
            Results{runs.length > 0 ? ` (${runs.length})` : ""}
          </h3>
          {runs.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setRuns([])} data-testid="button-clear-results">
              Clear results
            </Button>
          )}
        </div>
      )}

      {busy && (
        <Card>
          <CardContent className="py-6 flex items-center gap-3 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating with {generate.variables?.model ?? effectiveModel}...
          </CardContent>
        </Card>
      )}

      {runs.map((run) => (
        <Card key={run.id} data-testid={`copy-run-${run.id}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant={run.ok ? "secondary" : "destructive"}>
                  {run.ok ? (run.modelUsed || run.requestedModel) : run.requestedModel}
                </Badge>
                {!run.ok && <span className="text-xs text-red-600 font-medium">failed</span>}
                <span className="text-xs text-gray-400">{run.at} · {run.seconds.toFixed(1)}s</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRuns((prev) => prev.filter((r) => r.id !== run.id))}
                data-testid={`button-remove-run-${run.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {run.ok ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {run.versions!.map((v, i) => (
                  <div key={i} className="border rounded-lg p-3 flex flex-col" data-testid={`copy-variant-${run.id}-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-semibold text-gray-500">Variant {i + 1}</span>
                        <span className="text-xs text-gray-400" data-testid={`char-count-${run.id}-${i}`}>
                          {v.length} chars
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyVariant(v, `${run.id}-${i}`)}
                        data-testid={`button-copy-variant-${run.id}-${i}`}
                      >
                        {copiedKey === `${run.id}-${i}` ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{v}</p>
                  </div>
                ))}
              </div>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="break-words">{run.error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
